import React, { type FunctionComponent, useMemo } from 'react'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import {
  Input,
  PropertyContainer,
  PropertyRow,
  PropertyTitle,
} from '../../shared-styled-components/SharedStyledComponents'
import {
  AttributeManagementNextStepTypeEnum,
  type PipelineStep,
  type PipelineStepNextStepsRule,
} from '../../../types/attributeManagement.types.ts'

interface Props {
  step: PipelineStep
  onChange: (step: PipelineStep) => void
}

const MAX_NUM = 10
const MIN_NUM = 1

export const PipelineFormStepControlNumericRanges: FunctionComponent<Props> = ({
  step,
  onChange,
}) => {
  const i18n = useDomainMessages({
    range:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlNumericRanges.range',
    numberOfRanges:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlNumericRanges.numberOfRanges',
  })

  const nextStepsRule: PipelineStepNextStepsRule = useMemo(
    () => ({
      type: AttributeManagementNextStepTypeEnum.NumericRange,
      ranges: step?.nextStepsRule?.ranges ?? [],
      defaultNextPipelineStep: '',
      possibleNextSteps: step?.nextStepsRule?.possibleNextSteps ?? [],
    }),
    [step?.nextStepsRule?.possibleNextSteps, step?.nextStepsRule?.ranges]
  )

  return (
    <>
      <PropertyContainer style={{ flexDirection: 'column' }}>
        <PropertyRow>
          <PropertyTitle>{i18n.numberOfRanges}</PropertyTitle>
          <Input
            type={'number'}
            value={nextStepsRule.possibleNextSteps.length}
            onChange={e => {
              const amount = Math.min(MAX_NUM, Math.max(MIN_NUM, Number.parseInt(e.target.value)))

              const ranges: { rangeLow: number; rangeHigh: number; nextPipelineStep: string }[] = []

              const existingPossibleNextSteps = step?.nextStepsRule?.possibleNextSteps ?? []
              const possibleNextSteps = Array(amount)
                .fill('')
                .map((_, index) => existingPossibleNextSteps[index] ?? '')

              for (let i = 0; i < amount; i++) {
                ranges.push(
                  nextStepsRule?.ranges?.[i] ?? {
                    rangeHigh: 0,
                    rangeLow: 0,
                    nextPipelineStep: '',
                  }
                )
              }
              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  ranges: ranges,
                  possibleNextSteps: possibleNextSteps,
                },
              })
            }}
          />
        </PropertyRow>
        {(nextStepsRule.ranges ?? []).map((range, index) => (
          <PropertyRow key={`${index}`}>
            <PropertyTitle>
              {i18n.range} {index + 1}:
            </PropertyTitle>
            <Input
              type={'number'}
              value={range.rangeLow ?? 0}
              onChange={e => {
                if (!nextStepsRule.ranges) return
                nextStepsRule.ranges[index].rangeLow = Number.parseInt(e.target.value)
                onChange({
                  ...step,
                  nextStepsRule: nextStepsRule,
                })
              }}
            />
            <Input
              type={'number'}
              value={range.rangeHigh ?? 0}
              onChange={e => {
                if (!nextStepsRule.ranges) return
                nextStepsRule.ranges[index].rangeHigh = Number.parseInt(e.target.value)
                onChange({
                  ...step,
                  nextStepsRule: nextStepsRule,
                })
              }}
            />
          </PropertyRow>
        ))}
      </PropertyContainer>
    </>
  )
}
